import {
  store$,
  store
} from '@redux/store';
import {
  distinctUntilChanged,
  filter,
  map,
  fromEvent,
  merge,
  withLatestFrom,
  tap
} from 'rxjs';
import fastdom from 'fastdom';
import LoadingStatus from 'enums/loadingStatus';
import { onSearchChanged$ } from 'pages/searchResults/redux/observables';
import uiStateSlice from 'layout/redux/uiStateSlice';
import { mapStateSlice } from '../redux/slices';
import {
  selectSummaryCardsStatusAndMarkerData,
  selectSummaryCards,
  selectSummaryCardsInformation
} from '../redux/selectors';
import SummaryCardSkeleton from './skeletons/summaryCardSkeleton';
import getCard from '../services/processSummaryCard';
import SeeAllPlansCard from './summaryCard/seeAllPlansCard';
import { mapClicked$, mapDragStart$, onZoomChanged$ } from '../redux/observables';

const { actions } = uiStateSlice;
const mapActions = mapStateSlice.actions;
const totalPlansToShowInCarousel = 5;

/**
 * An object that holds the state of the Intersection Observer used to track the visibility of carousel cards.
 * @property {IntersectionObserver} instance - The Intersection Observer instance.
 * @property {number} previousCardPosition - The position of the previously visible card in the carousel.
 * @property {HTMLElement[]} observedElements - The carousel cards being observed by the Intersection Observer.
 */
const observer = {
  instance: null,
  previousCardPosition: null,
  observedElements: [],
};

/**
 * Creates an IntersectionObserver instance to track the visibility of carousel cards.
 * The observer callback dispatches a 'carouselScroll' event with information about the card's
 * position and direction of scroll when a card becomes visible.
 */
const createIntersectionObserver = () => {
  const observerOptions = {
    root: document.querySelector('[data-summary-card]'),
    threshold: 1.0, // Trigger only when 100% of the card is in view
    rootMargin: '0px', // Ensure no margin is considered
  };

  /**
   * Handles the callback for the Intersection Observer, which is used to track the visibility of the carousel cards.
   * @param {Array} entries - The array of observed elements and their intersection states.
   */
  const observerCallback = (entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        const state = store.getState();
        const card = entry.target;

        if (!card) {
          return;
        }

        const currentCardPosition = Array.from(card.parentNode.children).indexOf(card) + 1;
        let direction = null;

        if (observer.previousCardPosition !== null) {
          direction = observer.previousCardPosition < currentCardPosition ? 'right' : 'left';
        }

        observer.previousCardPosition = currentCardPosition;

        const listingId = card.getAttribute('data-id');
        const type = card.getAttribute('data-type');
        const summaryCards = selectSummaryCards(state);
        const activeItem = summaryCards?.find((listing) => listing.Id === Number(listingId));

        if (!direction && currentCardPosition === 1) {
          return;
        }

        const event = new CustomEvent('carouselScroll', {
          detail: {
            direction,
            currentCardPosition,
            nextCardPosition: currentCardPosition + 1,
            listingId,
            type,
            activeItem,
          },
        });

        document.dispatchEvent(event);
      }
    });
  };

  observer.instance = new IntersectionObserver(observerCallback, observerOptions);
}

/**
 * Observes the given cards using the Intersection Observer.
 * @param {Array} cards - The array of card elements.
 */
const observeCards = (cards) => {
  if (!observer.instance) {
    createIntersectionObserver();
  }
  
  while (observer.observedElements.length > 0) {
    const element = observer.observedElements.shift();
    observer.instance.unobserve(element);
  }
  
  observer.previousCardPosition = null;
  const elements = cards?.map((card) => card.getElement());
  elements?.forEach((card) => {
    observer.instance.observe(card);
    observer.observedElements.push(card);
  });
}

/**
 * Dispatches an action to clear all summary cards from the map state.
 *
 * This function triggers the `clearSummaryCards` action using the Redux store,
 * effectively removing all summary card data from the application's state.
 *
 * @function
 * @returns {void}
 */
const dispatchClearSummaryCardAction = () => {
  store.dispatch(mapActions.clearSummaryCards());
};

/**
 * Removes the Summary Card from the page.
 */
const closeSummaryCard = () => {
  const summaryCardContainer = document.querySelector('[data-map-carousel]');
  const summaryCard = document.querySelector('[data-summary-card]');

  if (summaryCard) {
    fastdom.mutate(() => {
      summaryCard.innerHTML = '';
      summaryCardContainer.setAttribute('hidden', '');
    });
  }
}

/**
 * Handles the display of a skeleton card while the actual summary card data is being loaded.
 */
const handleSummaryCardSkeleton = () => {
  const summaryCardContainer = document.querySelector('[data-map-carousel]');
  const closeButton = summaryCardContainer.querySelector('[data-summary-card-close]');
  const summaryCard = document.querySelector('[data-summary-card]');
  const skeleton = new SummaryCardSkeleton();

  fastdom.mutate(() => {
    summaryCardContainer.removeAttribute('hidden');
    summaryCard.innerHTML = skeleton.getHtml();
    closeButton?.setAttribute('hidden', true);
  });
}

/**
 * Handles the removal of a skeleton card when the ajax call failed.
 */
const removeSummaryCardSkeletonOnFail = () => {
  const summaryCardContainer = document.querySelector('[data-map-carousel]');
  const summaryCard = document.querySelector('[data-summary-card]');

  fastdom.mutate(() => {
    summaryCard.innerHTML = '';
    summaryCardContainer.setAttribute('hidden', true);
  });
}

/**
 * Subscribes to the summary card loading status in the Redux store. This ensures that a skeleton
 * loading indicator is displayed while the actual summary card data is being loaded.
 */
const onSummaryCardDataLoading$ = store$
  .pipe(
    map(selectSummaryCardsStatusAndMarkerData),
    filter((data) => data?.status === LoadingStatus.Loading.toString() && Boolean(data?.marker?.id)),
    distinctUntilChanged(),
  )

/**
 * Subscribes to the summary card loading status in the Redux store. This ensures that a skeleton
 * loading indicator is removed when the Ajax call failed.
 */
const onSummaryCardDataFailed$ = store$
.pipe(
  map(selectSummaryCardsStatusAndMarkerData),
  distinctUntilChanged(),
  filter((data) => data?.status === LoadingStatus.Failed.toString()),
)

/**
 * Emits the selected summary card data from the Redux store whenever it changes and is truthy.
 */
const onSummaryCardSelected$ = store$.pipe(
  map(selectSummaryCards),
  distinctUntilChanged(),
  filter(Boolean)
);

/**
 * Displays the summary cards.
 * @param {Array<Object>} listings - The listing objects.
 */
const renderSummaryCards = (listings) => {
  const container = document.querySelector('[data-summary-card]');
  
  fastdom.mutate(() => {
    container.innerHTML = '';
  });

  const cards = listings.map((listing) => getCard(listing));
  const isSeeAllPlansCardRequired = listings.length > 1 && cards[0].summaryCard.TotalPlans > totalPlansToShowInCarousel;
  const seeAllPlansCard =  isSeeAllPlansCardRequired ? new SeeAllPlansCard() : null;
  const fragment = document.createDocumentFragment();

  cards.forEach((card) => {
    const html = card.getHtml();
    const range = document.createRange();
    const documentFragment = range.createContextualFragment(html);
    fragment.appendChild(documentFragment);
  });

  /**
   * If the total number of plans exceeds the defined threshold, append a "See All Plans" card to the fragment.
   */
  if (isSeeAllPlansCardRequired) {
    seeAllPlansCard.setMetadata(cards[0].summaryCard);
    const seeAllPlansHtml = document.createRange().createContextualFragment(seeAllPlansCard.getHtml());
    fragment.appendChild(seeAllPlansHtml);
  } else {
    // If there is only one card, make it take full width
    const singleCard = fragment.querySelector('[data-summary-content]')
    singleCard?.classList.add('nhs-u-flex--0-0-full', 'nhs-u-max-width--full');
  }

  fastdom.mutate(() => {
    container.appendChild(fragment);
    cards.forEach((card) => {
      card.bindEvents?.();
    });

    if (isSeeAllPlansCardRequired) {
      seeAllPlansCard.bindEvents?.();
    }

    observeCards(cards);
  });
}

const onCardCtaClicked$ = fromEvent(document, 'summaryCardCtaClick');
onCardCtaClicked$.subscribe((card) => {
  const { type, id, name, url, variation } = card.detail;
  store.dispatch(
    actions.modalInformation({
      type,
      id,
      name,
      url,
      variation,
    })
  );
});

const selectedSummaryCardInformation$ = store$.pipe(map(selectSummaryCardsInformation), distinctUntilChanged());
const hamburgerMenuClicked$ = fromEvent(document.body, 'hamburgerMenuClicked');
const advancedFiltersClicked$ = fromEvent(document.body, 'advancedFiltersClicked');
const ListViewToggled$ = fromEvent(document.body, 'listViewToggled');
const onCloseSummaryCard$ = merge(
  hamburgerMenuClicked$,
  advancedFiltersClicked$,
  ListViewToggled$,
  mapClicked$,
  mapDragStart$,
  onZoomChanged$,
  onSearchChanged$
).pipe(
    withLatestFrom(selectedSummaryCardInformation$),
    tap(closeSummaryCard),
    /**
     * if the user had a summary card opened, and then taps on another marker to open a different summary card,
     * we should not close the current summary card, as it will be replaced by the new one.
     */
    filter(([, { status }]) => status !== LoadingStatus.Loading.toString()),
    filter(([, { cards }]) => cards?.length > 0)
  );

onSummaryCardDataLoading$.subscribe(handleSummaryCardSkeleton);
onSummaryCardDataFailed$.subscribe(removeSummaryCardSkeletonOnFail);
onSummaryCardSelected$.subscribe(renderSummaryCards);
onCloseSummaryCard$.subscribe(dispatchClearSummaryCardAction);